import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../../../components";
import {
  INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY,
  MAX_LENGTH_100,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  getValueOrBlank,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { pageId } from "../../../../../createUser/CreateUser";
import { ProjectInvoiceReversalFormKey } from "../../../../modelsAndSchemas/Models";
import { projectInvoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ProjectInvoiceReversalContainer } from "./ProjectInvoiceReversal.style";

type ProjectInvoiceReversalProps = {
  invoiceId: string;
};

const ProjectInvoiceReversal: React.FC<ProjectInvoiceReversalProps> = ({
  invoiceId,
}) => {
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { gridSizeLarge } = useGridContainer(true);
  const { getValues, control, setValue } = useFormContext();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const reversalReason = useWatch({
    control,
    name: ProjectInvoiceReversalFormKey.projectReversalReason,
  });

  const getLabel = (key: keyof typeof projectInvoiceHistorySiteCoreKeys) => {
    return fetchLabel(
      projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      projectInvoiceHistorySiteCoreKeys[key],
    );
  };

  useEffect(() => {
    if (reversalReason !== INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY) {
      setValue(ProjectInvoiceReversalFormKey.projectComments, "");
    }
  }, [reversalReason]);

  return (
    <ProjectInvoiceReversalContainer>
      <MuiTypography
        className="invoice-reversal-message"
        component="label"
        variant="h3"
      >
        {replaceHashWithValue(
          getLabel("areYouSureYouWantToReverseTheInvoice"),
          invoiceId,
        )}
      </MuiTypography>
      <MuiBox>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSizeLarge}>
            <FormController control={control} name="reversalReason">
              <MuiSelect
                label={getLabel("confirmationPopUpReversalReasonDropDown")}
                options={fetchDropdownValues(
                  projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                  projectInvoiceHistorySiteCoreKeys.confirmationPopUpInvoiceReversalReasonList,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ProjectInvoiceReversalFormKey.projectReversalReason}`}
                placeholder={commonLabels.selectPlaceHolder}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSizeLarge}>
            <MuiDateFieldFormController
              control={control}
              name={ProjectInvoiceReversalFormKey.projectReversalDate}
              id={`${pageId}-${ProjectInvoiceReversalFormKey.projectReversalDate}`}
              value={
                getValues(ProjectInvoiceReversalFormKey.projectReversalDate)
                  ? dayjs(
                      getValues(
                        ProjectInvoiceReversalFormKey.projectReversalDate,
                      ),
                    )
                  : dayjs()
              }
              label={getLabel("confirmationPopUpReversalDate")}
              format={userCountryDateFormat}
              className="date-picker"
              disabled
              disableLabel
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>
        </MuiGrid>
        {reversalReason === INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY && (
          <MuiBox className="comments">
            <FormController
              control={control}
              name={ProjectInvoiceReversalFormKey.projectComments}
            >
              <MuiTextField
                id={`${pageId}-${ProjectInvoiceReversalFormKey.projectComments}`}
                label={getLabel("confirmationPopUpCommentOther")}
                placeholder={getLabel(
                  "confirmationPopUpCommentBoxPlaceholderText",
                )}
                className="comments-input"
                multiline
                rows={4}
                data-testid={`${pageId}-${ProjectInvoiceReversalFormKey.projectComments}`}
                isMandatory
                requiredARIALabel={commonARIALabel.required}
                maxLength={MAX_LENGTH_100}
              />
            </FormController>
          </MuiBox>
        )}
      </MuiBox>
    </ProjectInvoiceReversalContainer>
  );
};

export default ProjectInvoiceReversal;
